import type { WorldSnapshot } from "@radar-sim/schema";
import type { RadarStateSource, Unsubscribe } from "./state-source.js";

export const RECORDING_SCHEMA_VERSION = 1 as const;

export interface RecordingMetadata {
  /** Stable id for the recording — caller-supplied or generated externally. */
  id: string;
  /** Scenario id captured. */
  scenarioId: string;
  /** Snapshot rate the recording was made at; same as the source's tickRateHz. */
  tickRateHz: number;
  /** ISO datetime when capture began. Wall clock; non-deterministic. */
  capturedAt: string;
  /** Optional human-readable label (e.g. session name). */
  label?: string;
}

export interface Recording {
  schemaVersion: typeof RECORDING_SCHEMA_VERSION;
  metadata: RecordingMetadata;
  snapshots: ReadonlyArray<WorldSnapshot>;
}

/**
 * Capture a snapshot stream from any RadarStateSource. The capture starts
 * the moment `start()` is called and ends on `stop()`, returning a Recording.
 *
 * Design notes:
 *   - This is a passive observer; it never sends commands to the source.
 *   - Snapshots are stored in memory; the host is responsible for persistence
 *     (R2 in classroom mode, IndexedDB locally).
 *   - The capture is deterministic w.r.t. the source: same scenario + same
 *     command stream → identical recordings.
 */
export class RecordingCapture {
  private readonly source: RadarStateSource;
  private snapshots: WorldSnapshot[] = [];
  private unsubscribe: Unsubscribe | undefined;
  private metadata: Omit<RecordingMetadata, "capturedAt"> & {
    capturedAt: string | undefined;
  };

  constructor(
    source: RadarStateSource,
    metadata: Omit<RecordingMetadata, "tickRateHz" | "capturedAt"> & {
      capturedAt?: string;
    },
  ) {
    this.source = source;
    this.metadata = {
      ...metadata,
      tickRateHz: source.tickRateHz,
      capturedAt: metadata.capturedAt,
    };
  }

  start(now: () => string = () => new Date().toISOString()): void {
    if (this.unsubscribe !== undefined) return;
    if (this.metadata.capturedAt === undefined) {
      this.metadata.capturedAt = now();
    }
    this.unsubscribe = this.source.subscribe((snapshot) => {
      this.snapshots.push(snapshot);
    });
  }

  stop(): Recording {
    if (this.unsubscribe !== undefined) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    if (this.metadata.capturedAt === undefined) {
      throw new Error("RecordingCapture.stop() called before start()");
    }
    return {
      schemaVersion: RECORDING_SCHEMA_VERSION,
      metadata: this.metadata as RecordingMetadata,
      snapshots: this.snapshots,
    };
  }

  get snapshotCount(): number {
    return this.snapshots.length;
  }
}
